import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { DASHBOARD_WIDGET_IDS, type DashboardWidgetId } from '@zeroth/shared';
import { WIDGET_COL_SPAN, WIDGET_ROW_SPAN } from '../features/dashboard/dashboardWidgetConfig';

const STORAGE_ORDER_KEY = 'zeroth-dashboard-order-v8';
const STORAGE_HIDDEN_KEY = 'zeroth-dashboard-hidden-v2';
const NEXT_ACTIONS_LAYOUT_MIGRATION_KEY = 'zeroth-dashboard-next-actions-layout-v1';
const GRID_COLUMN_COUNT = 12;

export const WIDGET_IDS = DASHBOARD_WIDGET_IDS;

export type WidgetId = DashboardWidgetId;

const DEFAULT_ORDER: WidgetId[] = [...WIDGET_IDS];

const DEFAULT_HIDDEN: WidgetId[] = [
  'kpi-active-skus', 'kpi-fba-units', 'kpi-inbound', 'kpi-on-order',
  'kpi-revenue', 'kpi-profit', 'kpi-roi',
  'system-alerts',
];

function warnStorageFailure(message: string, error: unknown) {
  if (import.meta.env.DEV) {
    console.warn(message, error);
  }
}

function loadOrder(): WidgetId[] {
  try {
    const raw = localStorage.getItem(STORAGE_ORDER_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as WidgetId[];
      const validIds = new Set<string>(WIDGET_IDS);
      const filtered = saved.filter((id) => validIds.has(id));
      const missing = DEFAULT_ORDER.filter((id) => !filtered.includes(id));
      const next = [
        ...filtered,
        ...missing.filter((id) => id !== 'next-actions'),
      ];
      if (missing.includes('next-actions')) {
        const inventoryHealthIndex = next.indexOf('inventory-health');
        next.splice(
          inventoryHealthIndex >= 0 ? inventoryHealthIndex + 1 : next.length,
          0,
          'next-actions',
        );
      }
      return next;
    }
  } catch (error) {
    warnStorageFailure('Failed to load dashboard widget order from storage; using defaults.', error);
  }
  return DEFAULT_ORDER;
}

function saveOrder(order: WidgetId[]) {
  try {
    localStorage.setItem(STORAGE_ORDER_KEY, JSON.stringify(order));
  } catch (error) {
    warnStorageFailure('Failed to persist dashboard widget order to storage.', error);
  }
}

function loadHidden(): Set<WidgetId> {
  try {
    const raw = localStorage.getItem(STORAGE_HIDDEN_KEY);
    const hidden = raw
      ? new Set(JSON.parse(raw) as WidgetId[])
      : new Set(DEFAULT_HIDDEN);
    if (localStorage.getItem(NEXT_ACTIONS_LAYOUT_MIGRATION_KEY) !== '1') {
      const hasSavedLayout = raw !== null || localStorage.getItem(STORAGE_ORDER_KEY) !== null;
      if (hasSavedLayout) hidden.add('next-actions');
      localStorage.setItem(NEXT_ACTIONS_LAYOUT_MIGRATION_KEY, '1');
    }
    return hidden;
  } catch (error) {
    warnStorageFailure('Failed to load hidden dashboard widgets from storage; using defaults.', error);
  }
  return new Set(DEFAULT_HIDDEN);
}

function saveHidden(hidden: Set<WidgetId>) {
  try {
    localStorage.setItem(STORAGE_HIDDEN_KEY, JSON.stringify([...hidden]));
  } catch (error) {
    warnStorageFailure('Failed to persist hidden dashboard widgets to storage.', error);
  }
}

type GridRect = {
  col: number;
  row: number;
  width: number;
  height: number;
};

type WidgetPlacement = GridRect & {
  id: WidgetId;
  index: number;
};

function gridRectEquals(a: GridRect, b: GridRect) {
  return a.col === b.col
    && a.row === b.row
    && a.width === b.width
    && a.height === b.height;
}

function getPlacementBlock(placements: WidgetPlacement[]): GridRect | null {
  if (placements.length === 0) return null;

  const col = Math.min(...placements.map((placement) => placement.col));
  const row = Math.min(...placements.map((placement) => placement.row));
  const right = Math.max(...placements.map((placement) => placement.col + placement.width));
  const bottom = Math.max(...placements.map((placement) => placement.row + placement.height));

  return {
    col,
    row,
    width: right - col,
    height: bottom - row,
  };
}

function placementsFillBlock(placements: WidgetPlacement[], block: GridRect) {
  const occupied = new Set<string>();

  for (const placement of placements) {
    for (let row = placement.row; row < placement.row + placement.height; row += 1) {
      for (let col = placement.col; col < placement.col + placement.width; col += 1) {
        if (
          col < block.col
          || col >= block.col + block.width
          || row < block.row
          || row >= block.row + block.height
        ) {
          return false;
        }
        occupied.add(`${col}:${row}`);
      }
    }
  }

  return occupied.size === block.width * block.height;
}

function simulateDenseGrid(visible: WidgetId[]): WidgetPlacement[] {
  const occupied: boolean[][] = [];

  const cellIsOpen = (col: number, row: number) => !occupied[row]?.[col];
  const canPlace = (col: number, row: number, width: number, height: number) => {
    if (col + width > GRID_COLUMN_COUNT) return false;

    for (let y = row; y < row + height; y += 1) {
      for (let x = col; x < col + width; x += 1) {
        if (!cellIsOpen(x, y)) return false;
      }
    }

    return true;
  };

  const markPlaced = (col: number, row: number, width: number, height: number) => {
    for (let y = row; y < row + height; y += 1) {
      occupied[y] ??= [];
      for (let x = col; x < col + width; x += 1) {
        occupied[y][x] = true;
      }
    }
  };

  return visible.map((id, index) => {
    const width = WIDGET_COL_SPAN[id];
    const height = WIDGET_ROW_SPAN[id];
    let placement: WidgetPlacement | null = null;

    for (let row = 0; placement === null; row += 1) {
      for (let col = 0; col <= GRID_COLUMN_COUNT - width; col += 1) {
        if (canPlace(col, row, width, height)) {
          placement = { id, index, col, row, width, height };
          markPlaced(col, row, width, height);
          break;
        }
      }
    }

    return placement;
  });
}

function buildFootprintSwapOrder(
  visible: WidgetId[],
  dragId: WidgetId,
  groupIds: WidgetId[]
) {
  const placements = simulateDenseGrid(visible);
  const placementById = new Map(placements.map((placement) => [placement.id, placement]));
  const dragPlacement = placementById.get(dragId);
  const targetPlacements = groupIds.map((id) => placementById.get(id)).filter(Boolean) as WidgetPlacement[];

  if (!dragPlacement || targetPlacements.length !== groupIds.length) return null;

  const sourceBlock = getPlacementBlock([dragPlacement]);
  const targetBlock = getPlacementBlock(targetPlacements);
  if (!sourceBlock || !targetBlock) return null;
  if (sourceBlock.width !== targetBlock.width || sourceBlock.height !== targetBlock.height) return null;
  if (!placementsFillBlock([dragPlacement], sourceBlock)) return null;
  if (!placementsFillBlock(targetPlacements, targetBlock)) return null;

  const swapSet = new Set<WidgetId>([dragId, ...groupIds]);
  const desiredById = new Map<WidgetId, GridRect>();

  desiredById.set(dragId, {
    col: targetBlock.col + (dragPlacement.col - sourceBlock.col),
    row: targetBlock.row + (dragPlacement.row - sourceBlock.row),
    width: dragPlacement.width,
    height: dragPlacement.height,
  });

  for (const placement of targetPlacements) {
    desiredById.set(placement.id, {
      col: sourceBlock.col + (placement.col - targetBlock.col),
      row: sourceBlock.row + (placement.row - targetBlock.row),
      width: placement.width,
      height: placement.height,
    });
  }

  const candidate = [...placements]
    .sort((a, b) => {
      const aRect = desiredById.get(a.id) ?? a;
      const bRect = desiredById.get(b.id) ?? b;
      return aRect.row - bRect.row || aRect.col - bRect.col || a.index - b.index;
    })
    .map((placement) => placement.id);

  const candidatePlacements = simulateDenseGrid(candidate);
  const candidatePlacementById = new Map(candidatePlacements.map((placement) => [placement.id, placement]));

  for (const placement of placements) {
    const actual = candidatePlacementById.get(placement.id);
    if (!actual) return null;

    const expected = desiredById.get(placement.id) ?? placement;
    if (!gridRectEquals(actual, expected)) return null;

    if (!swapSet.has(placement.id) && !gridRectEquals(actual, placement)) return null;
  }

  return candidate;
}

export function useDashboardLayout(options: { disabledWidgets?: WidgetId[] } = {}) {
  const [widgetOrder, setWidgetOrder] = useState<WidgetId[]>(loadOrder);
  const [isEditing, setIsEditing] = useState(false);
  const [hiddenWidgets, setHiddenWidgets] = useState<Set<WidgetId>>(loadHidden);
  const disabledWidgetSet = useMemo(
    () => new Set(options.disabledWidgets ?? []),
    [options.disabledWidgets]
  );

  const hiddenRef = useRef(hiddenWidgets);
  useEffect(() => { hiddenRef.current = hiddenWidgets; }, [hiddenWidgets]);
  const disabledRef = useRef(disabledWidgetSet);
  useEffect(() => { disabledRef.current = disabledWidgetSet; }, [disabledWidgetSet]);

  const widgetIsVisible = useCallback((id: WidgetId) => (
    !hiddenRef.current.has(id) && !disabledRef.current.has(id)
  ), []);

  const moveWidget = useCallback((visibleFromIndex: number, visibleToIndex: number) => {
    setWidgetOrder((prev) => {
      const visible = prev.filter(widgetIsVisible);
      const fromId = visible[visibleFromIndex];
      const toId = visible[visibleToIndex];
      if (!fromId || !toId || fromId === toId) return prev;

      const next = [...prev];
      const actualFrom = next.indexOf(fromId);
      const actualTo = next.indexOf(toId);
      if (actualFrom === -1 || actualTo === -1) return prev;

      next.splice(actualFrom, 1);
      // Removing the source shifts indexes left, so moving downward inserts after the target's new index.
      next.splice(next.indexOf(toId) + (actualFrom < actualTo ? 1 : 0), 0, fromId);
      saveOrder(next);
      return next;
    });
  }, [widgetIsVisible]);

  const swapWidgetGroup = useCallback((visibleDragIndex: number, visibleTargetIndices: number[]) => {
    setWidgetOrder((prev) => {
      const visible = prev.filter(widgetIsVisible);
      const dragId = visible[visibleDragIndex];
      const sortedTargetIndices = [...new Set(visibleTargetIndices)]
        .filter((i) => Number.isInteger(i))
        .sort((a, b) => a - b);
      const groupIds = sortedTargetIndices.map((i) => visible[i]).filter(Boolean);
      if (!dragId || groupIds.length === 0 || groupIds.includes(dragId)) return prev;

      const swapped = buildFootprintSwapOrder(visible, dragId, groupIds);
      if (!swapped) return prev;

      let vi = 0;
      const next = prev.map((id) => {
        if (widgetIsVisible(id)) return swapped[vi++];
        return id;
      });

      saveOrder(next);
      return next;
    });
  }, [widgetIsVisible]);

  const resetLayout = useCallback(() => {
    setWidgetOrder(DEFAULT_ORDER);
    saveOrder(DEFAULT_ORDER);
    const defaults = new Set(DEFAULT_HIDDEN);
    setHiddenWidgets(defaults);
    saveHidden(defaults);
  }, []);

  const toggleWidget = useCallback((id: WidgetId) => {
    setHiddenWidgets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveHidden(next);
      return next;
    });
  }, []);

  return {
    widgetOrder,
    isEditing,
    setIsEditing,
    moveWidget,
    swapWidgetGroup,
    resetLayout,
    hiddenWidgets,
    toggleWidget,
    visibleWidgets: widgetOrder.filter((id) => !hiddenWidgets.has(id) && !disabledWidgetSet.has(id)),
  };
}
